import { api, ApiError } from "../api.js";
import { esc, avatarHtml, displayName, formatDate, toast } from "../utils.js";
import { followButtonHtml, attachFollowHandlers } from "../followButton.js";
import { getCurrentUser } from "../auth.js";
import { navigate } from "../app.js";
import { confirmAction } from "../confirmDialog.js";

export async function render(container, params) {
  const communityId = params.id;
  const myId = getCurrentUser()?.id;
  container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;

  let community, members;
  // Admins only: the cohort's pending invitations.
  let invitations = [];

  async function load() {
    const [communityData, membersData] = await Promise.all([
      api.get(`/cohorts/${communityId}`),
      api.get(`/cohorts/${communityId}/members`),
    ]);
    community = communityData;
    members = membersData;
    invitations = community.my_role === "admin" ? await api.get(`/cohorts/${communityId}/invitations`) : [];
  }

  try {
    await load();
  } catch (err) {
    // A private cohort's members are for its members (the API says so).
    const message = err instanceof ApiError && err.code === "private_cohort" ? err.message : "Could not load members.";
    container.innerHTML = `<div class="dc-empty"><i class="bi ${err?.code === "private_cohort" ? "bi-lock" : "bi-exclamation-triangle"}"></i><div>${esc(message)}</div></div>`;
    return;
  }

  function adminCount() {
    return members.filter((m) => m.role === "admin").length;
  }

  async function runAction(userId, fn) {
    const row = container.querySelector(`[data-user-row="${userId}"]`);
    if (row) row.classList.add("opacity-50");
    try {
      await fn();
      await load();
      renderAll();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
      if (row) row.classList.remove("opacity-50");
    }
  }

  function promote(userId) {
    return runAction(userId, () => api.post(`/cohorts/${communityId}/members/${userId}/promote`));
  }

  function demote(userId) {
    return runAction(userId, () => api.post(`/cohorts/${communityId}/members/${userId}/demote`));
  }

  async function remove(userId) {
    const ok = await confirmAction({
      title: "Remove this member?",
      body: community.is_private
        ? "They'll need a new invitation to rejoin this private cohort."
        : "They can rejoin the cohort later if they want to.",
      confirmLabel: "Remove",
    });
    if (!ok) return;
    return runAction(userId, () => api.delete(`/cohorts/${communityId}/members/${userId}`));
  }

  function memberRowHtml(membership) {
    const isSelf = membership.user.id === myId;
    const isAdminRow = membership.role === "admin";
    const name = displayName(membership.user);
    const canDemote = adminCount() > 1;

    let actions = "";
    if (community.my_role === "admin" && !isSelf) {
      const buttons = [];
      if (isAdminRow) {
        buttons.push(
          `<button class="dropdown-item" data-action="demote" data-user-id="${membership.user.id}" ${canDemote ? "" : "disabled"}>
            <i class="bi bi-shield-minus me-2"></i>Remove admin${canDemote ? "" : " (last admin)"}
          </button>`
        );
      } else {
        buttons.push(
          `<button class="dropdown-item" data-action="promote" data-user-id="${membership.user.id}">
            <i class="bi bi-shield-plus me-2"></i>Make admin
          </button>`
        );
      }
      const canRemove = !isAdminRow || canDemote;
      buttons.push(
        `<button class="dropdown-item text-danger" data-action="remove" data-user-id="${membership.user.id}" ${canRemove ? "" : "disabled"}>
          <i class="bi bi-person-x me-2"></i>Remove from cohort${canRemove ? "" : " (last admin)"}
        </button>`
      );
      actions = `
        <div class="dropdown">
          <button class="btn btn-sm btn-outline-secondary" type="button" data-bs-toggle="dropdown">
            <i class="bi bi-three-dots"></i>
          </button>
          <div class="dropdown-menu dropdown-menu-end">${buttons.join("")}</div>
        </div>`;
    }

    return `
      <div class="d-flex align-items-center py-2 border-bottom dc-row-clickable"
           data-user-row="${membership.user.id}">
        ${avatarHtml(name, { className: "me-2", sizePx: 36, fontSize: "0.85rem" })}
        <div class="flex-grow-1">
          <!-- A real link, not just a clickable row: this is how the
               name is reached with a keyboard, and how it reads to a
               screen reader. The row click below is the convenience on
               top of it, for the other 95% of the row's area. -->
          <div class="fw-semibold small">
            <a class="text-reset text-decoration-none"
               href="#/user/${membership.user.id}">${esc(name)}</a>${isSelf ? " <span class=\"text-slate\">(you)</span>" : ""}
          </div>
          <div class="text-slate small">@${esc(membership.user.username)}</div>
        </div>
        ${isAdminRow ? `<span class="badge text-bg-light border me-2">Admin</span>` : ""}
        <!-- Before the admin menu so the row reads name / role / follow /
             manage. No ms-auto: flex-grow-1 on the name block already
             pushes everything after it to the right. -->
        ${(() => {
          const btn = followButtonHtml(membership.user, { extraClass: "" });
          return btn ? `<span class="me-2">${btn}</span>` : "";
        })()}
        ${actions}
      </div>`;
  }

  // ---- inviting (admins) ----

  function invitationRowHtml(inv) {
    const who = inv.invitee
      ? `${esc(displayName(inv.invitee))} <span class="text-slate">@${esc(inv.invitee.username)}</span>`
      : `<i class="bi bi-envelope me-1"></i>${esc(inv.email)}`;
    return `
      <div class="d-flex align-items-center py-2 border-bottom small" data-invitation-row="${inv.id}">
        <div class="flex-grow-1">${who}
          <div class="text-slate">Invited${inv.invited_by ? ` by ${esc(displayName(inv.invited_by))}` : ""} · expires ${esc(formatDate(inv.expires_at))}</div>
        </div>
        <button class="btn btn-sm btn-outline-secondary" data-cancel-invitation="${inv.id}">Cancel</button>
      </div>`;
  }

  function inviteSectionHtml() {
    return `
      <div class="card mb-4" id="invite-section">
        <div class="card-body">
          <h2 class="h6 fw-semibold mb-2"><i class="bi bi-person-plus me-1"></i>Invite people</h2>
          ${community.is_private ? `<p class="text-slate small mb-2">This cohort is private: invitations are the only way in.</p>` : ""}
          <label class="form-label small" for="invite-search">A DiveCohorts diver</label>
          <input type="search" class="form-control form-control-sm mb-1" id="invite-search" autocomplete="off"
            placeholder="Search by name or username" />
          <div id="invite-results" class="mb-3"></div>
          <form id="invite-email-form" class="mb-2" novalidate>
            <label class="form-label small" for="invite-email">Or an email address</label>
            <div class="input-group input-group-sm">
              <input type="email" class="form-control" id="invite-email" placeholder="name@example.com" autocomplete="off" />
              <button class="btn btn-outline-primary" type="submit" id="invite-email-send">Send invitation</button>
            </div>
            <div class="form-text">They get a link to accept, and can sign up first if they're new to DiveCohorts.</div>
          </form>
          <h3 class="h6 fw-semibold mt-3 mb-1">Pending invitations</h3>
          <div id="invitation-list">${invitations.length
            ? invitations.map(invitationRowHtml).join("")
            : `<p class="text-slate small mb-0">None.</p>`}</div>
        </div>
      </div>`;
  }

  async function reloadInvitations() {
    invitations = await api.get(`/cohorts/${communityId}/invitations`);
    const list = container.querySelector("#invitation-list");
    list.innerHTML = invitations.length ? invitations.map(invitationRowHtml).join("") : `<p class="text-slate small mb-0">None.</p>`;
    bindInvitationRows();
  }

  function bindInvitationRows() {
    container.querySelectorAll("[data-cancel-invitation]").forEach((btn) => {
      btn.addEventListener("click", async () => {
        btn.disabled = true;
        try {
          await api.delete(`/cohorts/${communityId}/invitations/${btn.dataset.cancelInvitation}`);
          await reloadInvitations();
        } catch (err) {
          toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
          btn.disabled = false;
        }
      });
    });
  }

  async function invite(body, button) {
    button.disabled = true;
    try {
      await api.post(`/cohorts/${communityId}/invitations`, body);
      toast(body.email ? `Invitation emailed to ${body.email}.` : "Invitation sent.", "success");
      await reloadInvitations();
      return true;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Something went wrong.", "danger");
      return false;
    } finally {
      button.disabled = false;
    }
  }

  function bindInviteSection() {
    const search = container.querySelector("#invite-search");
    const results = container.querySelector("#invite-results");
    let debounce;
    let generation = 0;
    const memberIds = new Set(members.map((m) => m.user.id));

    search.addEventListener("input", () => {
      clearTimeout(debounce);
      const term = search.value.trim();
      if (!term) {
        results.innerHTML = "";
        return;
      }
      debounce = setTimeout(async () => {
        const mine = ++generation;
        let users;
        try {
          users = await api.get("/users", { q: term });
        } catch {
          users = null;
        }
        if (mine !== generation) return; // a newer search already answered
        if (users === null) {
          results.innerHTML = `<p class="text-danger small mb-0">Could not search.</p>`;
          return;
        }
        const candidates = users.filter((u) => !memberIds.has(u.id));
        results.innerHTML = candidates.length
          ? candidates.slice(0, 8).map((u) => `
              <div class="d-flex align-items-center py-1 small">
                <div class="flex-grow-1">${esc(displayName(u))} <span class="text-slate">@${esc(u.username)}</span></div>
                <button class="btn btn-sm btn-outline-primary" data-invite-user="${u.id}">Invite</button>
              </div>`).join("")
          : `<p class="text-slate small mb-0">No one to invite matches "${esc(term)}".</p>`;
        results.querySelectorAll("[data-invite-user]").forEach((btn) => {
          btn.addEventListener("click", async () => {
            if (await invite({ user_id: Number(btn.dataset.inviteUser) }, btn)) {
              btn.textContent = "Invited";
              btn.disabled = true;
            }
          });
        });
      }, 250);
    });

    container.querySelector("#invite-email-form").addEventListener("submit", async (e) => {
      e.preventDefault();
      const input = container.querySelector("#invite-email");
      const email = input.value.trim();
      if (!email) return;
      if (await invite({ email }, container.querySelector("#invite-email-send"))) input.value = "";
    });
    bindInvitationRows();
  }

  function renderAll() {
    const isAdmin = community.my_role === "admin";
    container.innerHTML = `
      <button class="btn btn-link ps-0 mb-2" id="back-btn"><i class="bi bi-arrow-left"></i> Back</button>
      <h1 class="display-heading h4 mb-3">${esc(community.name)} — Members</h1>
      ${isAdmin ? inviteSectionHtml() : ""}
      <div>${members.map(memberRowHtml).join("")}</div>`;
    if (isAdmin) bindInviteSection();

    container.querySelector("#back-btn").addEventListener("click", () => history.back());

    // The whole row opens that diver's profile, except where an inner
    // control was clicked — the follow button, the admin menu and its
    // items all live inside the row, and promoting someone must not
    // also navigate away from the page you are managing.
    container.querySelectorAll("[data-user-row]").forEach((row) => {
      row.addEventListener("click", (e) => {
        if (e.target.closest("a, button, .dropdown-menu, [data-bs-toggle]")) return;
        navigate(`/user/${row.dataset.userRow}`);
      });
    });
    // Re-bound on each renderAll(), which rewrites the whole roster after
    // a promote/demote/remove.
    attachFollowHandlers(container);
    container.querySelectorAll("[data-action]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const userId = Number(btn.dataset.userId);
        if (btn.dataset.action === "promote") promote(userId);
        if (btn.dataset.action === "demote") demote(userId);
        if (btn.dataset.action === "remove") remove(userId);
      });
    });
  }

  renderAll();
}
